import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { designShowcaseService } from "../services/designShowcaseService";
import { designShowcaseDisplayId } from "./DesignShowcaseCommonStore";
import { DesignShowcase } from "../../../api/graphql";
import { showcaseCategoryDisplayId } from "../../showcaseCategory/store/ShowcaseCategoryCommonStore";
import { diaDeCalendario } from "../../../common/calendarDate";
import { getShowcaseLineValues } from "../../showcaseLine/store/ShowcaseLineCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { ShowcaseStatus } from "../../../api/graphql";
import { showcaseCategoryService } from "../../showcaseCategory/services/showcaseCategoryService";
import { InitializeParams } from "../../../store/types";

export class DesignShowcaseViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = designShowcaseService;
  designShowcaseInstance: DesignShowcase | null = null;
  cardState: Record<string, boolean> = {
    identification: true,
    classification: true,
    numbers: true,
    dates: true,
    flags: true,
    contact: true,
    documents: true,
    richContent: true,
    advanced: true,
  };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  codeValue: string = "";
  nameValue: string = "";
  summaryValue: string = "";
  statusValue: ShowcaseStatus = "" as ShowcaseStatus;
  categoryService = showcaseCategoryService;
  categorySearchValue: string = "";
  categoryIdValue: string = "";
  categorySelectedValue: { displayId: string; id: string } | null = null;
  relatedCategoriesService = showcaseCategoryService;
  relatedCategoriesSearchValue: string = "";
  relatedCategoriesIdValue: string = "";
  relatedCategoriesSelectedValues: { displayId: string; id: string }[] = [];
  appliedStatusesValue: ShowcaseStatus[] = [];
  lifecycleValue: string = "";
  quantityValue: number = 0;
  durationInYearsValue: number | undefined = undefined;
  amountValue: number = 0;
  ratioValue: number | undefined = undefined;
  startDateValue: string | null = null;
  expiresAtValue: Date | null = null;
  registeredAtValue: Date | null = null;
  activeValue: boolean = false;
  highlightedValue: boolean = false;
  phoneValue: string = "";
  taxIdValue: string = "";
  attachmentFiles: CrudRows = {};
  anyFileFiles: CrudRows = {};
  avatarFiles: CrudRows = {};
  keywordsValue: string[] = [];
  bodyValue: string = "";
  metadataValue: string = "{}";
  internalNoteValue: string = "";
  serverLabelValue: string = "";
  linesColumnWidths: Record<string, number | string> = {};
  linesRows: CrudRows = {};
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.linesColumnWidths = { concept: 200, quantity: 200, unitPrice: 200 };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadDesignShowcaseById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.designShowcaseOne({ id });
    this.designShowcaseInstance = result.designShowcaseOne;
    if (this.designShowcaseInstance) {
      this.loadValues(this.designShowcaseInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.designShowcaseInstance) {
      return designShowcaseDisplayId(
        this.designShowcaseInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.DesignShowcaseSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  viewFormCategory() {
    const id = this.designShowcaseInstance?.category?.id;
    if (id) {
      this.rootStore.openOrPushOverlay("showcaseCategory", id, "view");
    }
  }

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "category") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.showcaseCategoryOne({
          id: params.returnData.resultId,
        });
        const showcaseCategory = result.showcaseCategoryOne;
        this.categorySelectedValue = showcaseCategory;
      } else if (params.callerData?.fieldName === "relatedCategories") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.showcaseCategoryOne({
          id: params.returnData.resultId,
        });
        const showcaseCategory = result.showcaseCategoryOne;
        this.relatedCategoriesSelectedValues.push(showcaseCategory);
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getStatusOptions() {
    return this.langStore.ShowcaseStatusOptions;
  }

  get categoryValue() {
    if (this.categorySelectedValue) {
      return {
        id: this.categorySelectedValue.id,
        displayId: showcaseCategoryDisplayId(
          this.categorySelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get relatedCategoriesValues() {
    return this.relatedCategoriesSelectedValues.map((x) => {
      return {
        id: x.id,
        displayId: showcaseCategoryDisplayId(x, this.langStore),
      };
    });
  }

  getAppliedStatusesOptions() {
    return this.langStore.ShowcaseStatusOptions;
  }

  get linesVisibleColumns() {
    return [
      {
        name: "concept",
        title: this.langStore.ShowcaseLineConceptLabel,
        width: this.linesColumnWidths["concept"],
      },
      {
        name: "quantity",
        title: this.langStore.ShowcaseLineQuantityLabel,
        width: this.linesColumnWidths["quantity"],
      },
      {
        name: "unitPrice",
        title: this.langStore.ShowcaseLineUnitPriceLabel,
        width: this.linesColumnWidths["unitPrice"],
      },
    ];
  }

  linesSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.linesColumnWidths = values;
  }

  get linesRowsDyn() {
    return this.linesRows;
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "DesignShowcase",
      fieldName: fieldName,
      instanceId: this.designShowcaseInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "DesignShowcase",
        fieldName: fieldName,
        instanceId: this.designShowcaseInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.designShowcaseInstance = null;
    this.codeValue = "";
    this.nameValue = "";
    this.summaryValue = "";
    this.statusValue = "" as ShowcaseStatus;
    this.categorySearchValue = "";
    this.categoryIdValue = "";
    this.relatedCategoriesSearchValue = "";
    this.relatedCategoriesIdValue = "";
    this.relatedCategoriesSelectedValues = [];
    this.appliedStatusesValue = [];
    this.lifecycleValue = "";
    this.quantityValue = 0;
    this.durationInYearsValue = undefined;
    this.amountValue = 0;
    this.ratioValue = undefined;
    this.startDateValue = null;
    this.expiresAtValue = null;
    this.registeredAtValue = null;
    this.activeValue = false;
    this.highlightedValue = false;
    this.phoneValue = "";
    this.taxIdValue = "";
    this.attachmentFiles = {};
    this.anyFileFiles = {};
    this.avatarFiles = {};
    this.keywordsValue = [];
    this.bodyValue = "";
    this.metadataValue = "{}";
    this.internalNoteValue = "";
    this.serverLabelValue = "";
    this.linesRows = {};
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelDesignShowcase() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("designShowcase_update");
  }

  editDesignShowcase() {
    const id = this.designShowcaseInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/designShowcase/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printDesignShowcase(handlePrint: () => void): void {
    handlePrint();
  }

  *closeDesignShowcase(): any {
    yield this.close();
  }

  loadValues(instance: DesignShowcase) {
    this.idValue = instance.id;
    this.codeValue = instance.code;
    this.nameValue = instance.name;
    this.summaryValue = instance.summary || "";
    this.statusValue = instance.status;
    this.categorySelectedValue = instance.category;
    this.relatedCategoriesSelectedValues = instance.relatedCategories
      ? instance.relatedCategories
      : [];
    this.appliedStatusesValue = instance.appliedStatuses || [];
    this.lifecycleValue = instance.lifecycle?.id || "";
    this.quantityValue = instance.quantity;
    this.durationInYearsValue = instance.durationInYears || undefined;
    this.amountValue = instance.amount;
    this.ratioValue = instance.ratio || undefined;
    this.startDateValue = diaDeCalendario(instance.startDate);
    this.expiresAtValue = instance.expiresAt
      ? new Date(instance.expiresAt)
      : null;
    this.registeredAtValue = instance.registeredAt
      ? new Date(instance.registeredAt)
      : null;
    this.activeValue = instance.active;
    this.highlightedValue = instance.highlighted;
    this.phoneValue = instance.phone || "";
    this.taxIdValue = instance.taxId || "";
    this.attachmentFiles = instance.attachment_data
      ? {
          [instance.attachment_data.name]: {
            name: instance.attachment_data.name,
            formattedSize: formatBytes(instance.attachment_data?.size || 0),
            mimetype: instance.attachment_data?.mimetype,
            id: instance.attachment_data.name,
            __status__: "original",
          },
        }
      : {};
    this.anyFileFiles = instance.anyFile_data
      ? {
          [instance.anyFile_data.name]: {
            name: instance.anyFile_data.name,
            formattedSize: formatBytes(instance.anyFile_data?.size || 0),
            mimetype: instance.anyFile_data?.mimetype,
            id: instance.anyFile_data.name,
            __status__: "original",
          },
        }
      : {};
    this.avatarFiles = instance.avatar_data
      ? {
          [instance.avatar_data.name]: {
            name: instance.avatar_data.name,
            formattedSize: formatBytes(instance.avatar_data?.size || 0),
            mimetype: instance.avatar_data?.mimetype,
            id: instance.avatar_data.name,
            __status__: "original",
          },
        }
      : {};
    this.keywordsValue = instance.keywords || [];
    this.bodyValue = instance.body || "";
    this.metadataValue = instance.metadata || "";
    this.internalNoteValue = instance.internalNote || "";
    this.serverLabelValue = instance.serverLabel || "";
    this.linesRows = instance.lines
      ? Object.assign(
          {},
          ...instance.lines.map((x) => {
            const processedRow = getShowcaseLineValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadDesignShowcaseById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
